import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO } from "../data";
import GalleryLightbox from "../components/GalleryLightbox";
import ContactForm from "../components/ContactForm";

export default function Referencer() {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fallback images if CMS fetch fails
  const fallbackImages = [
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/showroom-floor.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/craftsman-working.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/reference-gallery-1.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/reference-gallery-2.jpg"
  ];

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/by-slug/da/referencer");
        if (res.ok) {
          const data = await res.json();
          setGallery(data);
        }
      } catch (err) {
        console.error("Fejl ved hentning af referencer fra CMS:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchGallery();
  }, []);

  const galleryImages = gallery?.images || fallbackImages;
  const galleryTitle = gallery?.title?.da || "Referenceprojekter";
  const galleryDesc = gallery?.description?.da || "Et udvalg af vores afsluttede projekter i hele Danmark. Vi leverer altid højeste kvalitet og præcision til aftalt tid.";

  return (
    <>
      <Helmet>
        <title>Referencer & Galleri | Se Vores Udførte Gulve</title>
        <meta
          name="description"
          content="Se billeder af vores afsluttede projekter med epoxygulve, industrigulve og fugefrie belægninger i Halsnæs og resten af Sjælland. Bliv inspireret her."
        />
        <meta property="og:title" content="Referencer & Galleri | Se Vores Udførte Gulve" />
        <meta
          property="og:description"
          content="Se billeder af vores afsluttede projekter med epoxygulve, industrigulve og fugefrie belægninger i Halsnæs og resten af Sjælland. Bliv inspireret her."
        />
        <meta property="og:image" content={fallbackImages[0]} />
      </Helmet>

      {/* Page Header */}
      <section className="bg-brand-blue-900 text-white pt-48 pb-28 sm:pt-44 sm:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={fallbackImages[0]}
            alt="Referencer baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-blue-300 mb-3">
              Galleri & Cases
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Referenceprojekter
            </h1>
            <p className="text-lg text-brand-blue-100 mt-4 max-w-2xl">
              Vi er stolte af vores arbejde. Se et udvalg af vores professionelt udførte fugefrie gulve hos tilfredse kunder.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Gallery and Description */}
            <div className="lg:col-span-7 space-y-8">
              <div className="prose prose-slate max-w-none">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-blue-900 mb-4">
                  Kvalitetsarbejde, der taler for sig selv
                </h2>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Hos Halsnæs Epoxygulve udfører vi alle opgaver med den største faglige stolthed. Vi ved, at et fugefrit gulv kræver absolut præcision i forberedelsen, blandingen af materialer og selve udlægningen for at opnå et fejlfrit resultat.
                </p>
                <p className="text-base text-concrete-600 leading-relaxed mb-6">
                  Vores referencer dækker alt fra mindre private garager og bryggerser til store erhvervsbyggerier, showrooms og industrilokaler. Klik på billederne i galleriet nedenfor for at se dem i stor visning.
                </p>
              </div>

              {/* Dynamic CMS Gallery */}
              <div className="pt-6 border-t border-concrete-100">
                <h3 className="text-xl font-bold text-brand-blue-900 mb-2">
                  {galleryTitle}
                </h3>
                <p className="text-sm text-concrete-500 mb-6">
                  {galleryDesc}
                </p>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-blue-600"></i>
                  </div>
                ) : (
                  <GalleryLightbox images={galleryImages} title={galleryTitle} />
                )}
              </div>
            </div>

            {/* Right: Sidebar with Contact Form */}
            <div className="lg:col-span-5 w-full lg:sticky lg:top-28">
              <ContactForm source="Referencer side" />
              
              {/* Quick Contact Box */}
              <div className="mt-6 rounded-2xl bg-concrete-50 p-6 border border-concrete-100 text-center space-y-4">
                <h4 className="font-bold text-brand-blue-900">Vil du have et lignende resultat?</h4>
                <p className="text-xs text-concrete-500">
                  Kontakt Simon Babiel i dag for at drøfte dine muligheder og få et uforpligtende tilbud.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href={`tel:+45${CONTACT_INFO.phone}`}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-concrete-200 py-2.5 text-sm font-bold text-brand-blue-900 hover:bg-concrete-50"
                  >
                    <i className="fa-solid fa-phone"></i>
                    Tlf: {CONTACT_INFO.phone}
                  </a>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-concrete-200 py-2.5 text-sm font-bold text-brand-blue-900 hover:bg-concrete-50"
                  >
                    <i className="fa-solid fa-envelope"></i>
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Bottom CTA Block */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/reference-gallery-2.jpg"
            alt="Referenceprojekt"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-brand-dark/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            Lad os skabe dit næste referenceprojekt
          </h2>
          <p className="text-base text-slate-200 max-w-2xl mx-auto mb-8">
            Uanset opgavens størrelse leverer vi altid samme høje kvalitet, præcision og gode service.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
            >
              Få et tilbud
            </Link>
            <a
              href={`tel:+45${CONTACT_INFO.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-white/10 transition-all"
            >
              <i className="fa-solid fa-phone"></i>
              Ring til Simon
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
